import React, { FC } from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { SOCIAL_SETTINGS } from './socialSettings';

import Favicon32 from '../../../public/assets/favicon-32x32.png';
import Favicon16 from '../../../public/assets/favicon-16x16.png';
import FaviconIco from '../../../public/assets/favicon.ico';
import SafariPinnedTab from '../../../public/assets/safari-pinned-tab.svg';
import BrowserconfigXML from '../../../public/assets/browserconfig.xml';

import { MAIN_SITE_LINK } from '../../../constants';
import {
    OpenGraphItems,
    OpenGraphItem,
    ExtraLinkItems,
    ExtraLinkItem,
    ExtraMetaTags
} from './types';

const isSearchOrHomepagePath = (path: string) => {
    return (
        path.includes('for-sale') || path.includes('to-rent') || path === '/'
    );
};

export const buildCanonicalUrl = (path: string) => {
    const pathWithoutForwardSlashPrefix = path.replace(/^\//g, '');

    return `${MAIN_SITE_LINK}/${pathWithoutForwardSlashPrefix}`;
};

export const getSocialSharingTags = (path: string) => {
    const iPhoneUrl = `${SOCIAL_SETTINGS.IPHONE_BASE_URL}${path}`;
    const twitterPlayUrl = `${SOCIAL_SETTINGS.TWITTER_PLAY_BASE_URL}${path}`;
    return (
        <>
            <meta name="twitter:card" content="summary" />
            <meta
                name="twitter:app:name:iphone"
                content={SOCIAL_SETTINGS.TWITTER_IOS_APP_NAME}
            />
            <meta
                name="twitter:app:id:iphone"
                content={SOCIAL_SETTINGS.IOS_APP_ID}
            />
            <meta
                name="twitter:app:name:googleplay"
                content={SOCIAL_SETTINGS.TWITTER_ANDROID_APP_NAME}
            />
            <meta
                name="twitter:app:id:googleplay"
                content={SOCIAL_SETTINGS.ANDROID_APP_ID}
            />
            <meta name="twitter:app:url:iphone" content={iPhoneUrl} />
            <meta name="twitter:app:url:googleplay" content={twitterPlayUrl} />
            <meta property="fb:admins" content={SOCIAL_SETTINGS.FB_ADMINS} />
            <meta
                property="twitter:account_id"
                content={SOCIAL_SETTINGS.TWITTER_ACCOUNT_ID}
            />
        </>
    );
};

export const generateOpenGraphTags = (items: OpenGraphItems) => {
    return (
        <>
            {items.map((item: OpenGraphItem, idx: number) => (
                <meta
                    key={`og-${idx}`}
                    property={item.property}
                    content={item.content}
                />
            ))}
        </>
    );
};

export const generateExtraLinkTags = (items: ExtraLinkItems) => {
    return (
        <>
            {items.map((item: ExtraLinkItem, idx: number) => {
                const { href, rel, crossOrigin } = item;

                const hasCrossOrigin = typeof crossOrigin === 'string';
                const crossOriginInfo = hasCrossOrigin ? { crossOrigin } : {};

                return (
                    <link
                        key={`link-${idx}`}
                        href={href}
                        rel={rel}
                        {...crossOriginInfo}
                    />
                );
            })}
        </>
    );
};

type Props = {
    pageTitle: string;
    pageDescription: string;
    canonicalPath: string;
    index?: () => boolean;
    schema?: () => string;
    openGraphValues?: OpenGraphItems;
    extraLinkTags?: ExtraLinkItems;
    extraMetaTags?: ExtraMetaTags;
};

const Head: FC<Props> = ({
    pageTitle,
    pageDescription,
    canonicalPath,
    index,
    schema,
    openGraphValues,
    extraLinkTags,
    children,
    extraMetaTags
}) => {
    const router = useRouter();
    const { asPath } = router;

    return (
        <NextHead>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {canonicalPath && (
                <link rel="canonical" href={buildCanonicalUrl(canonicalPath)} />
            )}
            <link rel="icon" type="image/png" href={Favicon32} sizes="32x32" />
            <link rel="icon" type="image/png" href={Favicon16} sizes="16x16" />
            <link rel="shortcut icon" href={FaviconIco} />
            <link rel="mask-icon" href={SafariPinnedTab} color="#6a148e" />
            {extraLinkTags ? generateExtraLinkTags(extraLinkTags) : null}
            <meta name="msapplication-config" content={BrowserconfigXML} />
            <meta name="theme-color" content="#ffffff" />
            {isSearchOrHomepagePath(asPath) && getSocialSharingTags(asPath)}
            {openGraphValues && openGraphValues.length > 0
                ? generateOpenGraphTags(openGraphValues)
                : null}
            {index && !index() ? (
                <meta name="robots" content="noindex, follow" />
            ) : null}
            {extraMetaTags
                ? extraMetaTags.map(({ name, content }, index) => (
                      <meta
                          key={`${name}-${index}`}
                          name={name}
                          content={content}
                      />
                  ))
                : null}
            {schema ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: schema()
                    }}
                />
            ) : null}
            {children}
        </NextHead>
    );
};

export default Head;
